/**
 * Per-product trading eligibility for the signed-in investor.
 *
 * The rules themselves live in the backend: product search and
 * `GET /v3/investor/product-restrictions/...` both evaluate the signed-in
 * user's profile (`customer_country` plus `isretirement`) against the product's
 * rolled-up contract remarks and emit `restrictionFlags`. The client never
 * re-derives that policy -- a second copy of it drifted from the backend once
 * already (it warned every non-US account about US ETFs, which only applies to
 * a small country list) -- so `restrictionFlags` is the single client-side
 * signal for "this product is restricted for this account". See
 * `utils/productRestrictions.ts` and `hooks/useProductTradability.ts`.
 *
 * What stays here is the account country (header copy only) and the shared
 * add-blocked strings.
 */

/** `user_configuration.data` key holding the customer's account country. */
export const CUSTOMER_COUNTRY_CONFIG_KEY = "customer_country";

/** Shown to the investor wherever adding is blocked for tradability reasons. */
export const PRODUCT_NOT_TRADABLE_MESSAGE =
  "This product contains contracts which are not tradable for your account.";

/**
 * Pre-existing generic copy for the other add-blocking reasons (not investible,
 * retirement-account mismatch). Lives here so every "why can't I add this?"
 * surface reads both strings from one place.
 */
export const PRODUCT_BLOCKED_CRITERIA_MESSAGE =
  "This product does not meet the risk criteria set by Plutus";

/**
 * Normalizes the raw config value. Anything not a usable string collapses to
 * null, which display surfaces render as "Not configured (treated as US)".
 */
export function normalizeCustomerCountry(
  value: string | null | undefined
): string | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim().toLowerCase();
  return normalized.length > 0 ? normalized : null;
}
